/**
 * Release identity and negotiation over REST (D37, docs/compatibility.md §4.1, §4.7): who is calling, which API a
 * host speaks, and the floor below which the cloud refuses an SPA build.
 *
 * **`x-acp-client`** — every SPA and CLI request says what it is:
 *
 *     x-acp-client: web/<commit>; built=<ISO timestamp>
 *     x-acp-client: web-node/<commit>; built=<ISO timestamp>   (the SPA a node serves: its node's release)
 *     x-acp-client: cli/<version>+<commit>; built=<ISO timestamp>
 *
 * `<kind>/<version>` is the client release (`clientRelease`), the unit the per-release caller metric counts and
 * a deprecation's removal waits on (§4.8). `built` is when the build was made (a commit's date for a CLI run from
 * source); a client that cannot tell leaves it out, and its age is then unknown — never refused. The header is
 * informational everywhere except the cloud's SPA floor (`webClientRefused`).
 *
 * **CORS.** A browser sends the header only to a host that allows it in `Access-Control-Allow-Headers`
 * (`@acp/cloud-link/cors.js`): a host that does not would fail every preflight. So the SPA sends it only to a host
 * whose `/health` lists `client-header` (`API_FEATURES`) — an older node, or a cloud rolled back, is sent none.
 *
 * **`/health.api`** — `{ level, features }`: a newer client feature-detects against an older server and hides what
 * it lacks (§4.1's "Node API ← cloud SPA / CLI" row). `level` counts contract generations of this negotiation (1:
 * this wave); `features` names what a host serves beyond its level, read as a set — a name a reader does not know is
 * ignored, never refused.
 */
import { z } from 'zod';

/** The request header every SPA and CLI request carries (lower case: HTTP header names are case-insensitive). */
export const CLIENT_HEADER = 'x-acp-client';

/** The negotiation's level (`/health.api.level`). Bumped only with a new negotiation rule, not with each feature. */
export const API_LEVEL = 1;

/**
 * What a host's REST API offers beyond its level (`/health.api.features`):
 *
 * - `client-header` — the host allows `x-acp-client` through CORS (both hosts, from this wave);
 * - `deprecation` — deprecated routes answer `Deprecation` / `Sunset` headers (both hosts);
 * - `client-floor` — the host refuses an SPA build below its floor with `426 upgrade-required` (the cloud);
 * - `cloud-release-hold` — `POST /cloud-productions` takes `releaseHold` (D37 §4.1: a scheduled production re-pins at
 *   launch unless held); an older cloud refuses the field (it reads requests strictly).
 */
export const API_FEATURES = ['client-header', 'deprecation', 'client-floor', 'cloud-release-hold'] as const;
export type ApiFeatureWire = (typeof API_FEATURES)[number];

/** The features the cloud API lists, and a node's state service. */
export const CLOUD_API_FEATURES: readonly ApiFeatureWire[] = ['client-header', 'deprecation', 'client-floor', 'cloud-release-hold'];
export const NODE_API_FEATURES: readonly ApiFeatureWire[] = ['client-header', 'deprecation'];

/**
 * A feature list as a reader takes it: any strings (a name this build does not know is kept and ignored), bounded so
 * a hostile host cannot make a reader hold a megabyte of them. Shared with the site link's `features` (`siteLink.ts`).
 */
export const featureListWireSchema = z.array(z.string().min(1).max(64)).max(256);

/** `/health.api`. Absent from a host older than this wave: it offers none of the features. */
export const healthApiWireSchema = z
  .object({
    level: z.number().int().nonnegative(),
    features: featureListWireSchema,
  })
  .strict();
export type HealthApiWire = z.infer<typeof healthApiWireSchema>;

/** The client kinds a header names. Anything else is still counted (its release is its text), just never judged. */
export const CLIENT_KINDS = ['web', 'web-node', 'cli'] as const;

/** A parsed `x-acp-client`. */
export type ClientIdentityWire = {
  /** `web`, `cli`, or another kind a newer client sends. */
  readonly kind: string;
  readonly version: string;
  /** `<kind>/<version>`: what the caller metric counts by. */
  readonly release: string;
  /** When the build was made, epoch ms; null when the client did not say. */
  readonly builtAtMs: number | null;
};

const HEADER_PATTERN = /^([a-z][a-z0-9-]{0,15})\/([A-Za-z0-9._+-]{1,80})(?:;\s*built=(\S{1,40}))?$/;

/**
 * The header's value, or null for none or one this parser cannot read (such a request is served as if it carried
 * none: the header is informational, and a malformed one must never fail a request).
 */
export function parseClientHeader(value: string | undefined | null): ClientIdentityWire | null {
  if (value === undefined || value === null) return null;
  const match = HEADER_PATTERN.exec(value.trim());
  if (match === null) return null;
  const [, kind, version, built] = match;
  if (kind === undefined || version === undefined) return null;
  const builtAtMs = built === undefined ? null : isoMs(built);
  return { kind, version, release: `${kind}/${version}`, builtAtMs };
}

function isoMs(text: string): number | null {
  if (!z.iso.datetime({ offset: true }).safeParse(text).success) return null;
  const ms = Date.parse(text);
  return Number.isNaN(ms) ? null : ms;
}

/** The header's value for a build: `<kind>/<version>`, and `; built=<ISO>` when the build time is known. */
export function clientHeaderValue(kind: (typeof CLIENT_KINDS)[number], version: string, builtAtMs: number | null): string {
  const cleaned = version.replace(/[^A-Za-z0-9._+-]/g, '-').slice(0, 80);
  const safe = cleaned === '' ? 'unknown' : cleaned;
  return builtAtMs === null ? `${kind}/${safe}` : `${kind}/${safe}; built=${new Date(builtAtMs).toISOString()}`;
}

/**
 * The SPA floor's refusal (§4.1): `426 Upgrade Required` on any user route of the cloud, before the route runs. Its
 * body has the `ErrorResponse` shape with a code OUTSIDE `ERROR_CODES` (`errors.ts`): every reader decodes error
 * bodies loosely (`decodeError` keeps any code), and a new value in that closed response enum would be a break for
 * the older readers the gate models. Clients branch on the status. On the messages list (`messages.ts`).
 */
export const UPGRADE_REQUIRED_STATUS = 426;
export const UPGRADE_REQUIRED_CODE = 'upgrade-required';

export const upgradeRequiredResponseWireSchema = z
  .object({
    error: z
      .object({
        code: z.literal(UPGRADE_REQUIRED_CODE),
        message: z.string(),
        issues: z.array(z.object({ path: z.string(), message: z.string() }).strict()),
        retryAfterSeconds: z.null(),
      })
      .strict(),
  })
  .strict();
export type UpgradeRequiredResponseWire = z.infer<typeof upgradeRequiredResponseWireSchema>;

/** The SPA's window (§4.1): a build older than this, measured against the floor the cloud is configured with, is refused. */
export const WEB_CLIENT_WINDOW_DAYS = 14;
/** The CLI's window (§4.1), and when it starts warning. A CLI is never refused. */
export const CLI_CLIENT_WINDOW_DAYS = 90;
export const CLI_CLIENT_WARN_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Whether the cloud refuses this client with `426 upgrade-required`: an SPA build made before `floorMs` (the cloud's
 * configured floor; null: none). A CLI, a build that did not say when it was made, and every other kind are served —
 * `web-node` too: a node serves its own release's build, old by design on a pinned node, and a reload cannot change it.
 */
export function webClientRefused(client: ClientIdentityWire | null, floorMs: number | null): boolean {
  if (client === null || floorMs === null || client.kind !== 'web' || client.builtAtMs === null) return false;
  return client.builtAtMs < floorMs;
}

/** What a CLI says of its own age (§4.1): nothing inside 30 days, a warning past it, a stronger one outside the 90-day window. */
export function cliAgeWarning(builtAtMs: number | null, nowMs: number): string | null {
  if (builtAtMs === null) return null;
  const days = Math.floor((nowMs - builtAtMs) / DAY_MS);
  if (days > CLI_CLIENT_WINDOW_DAYS) {
    return `this acp CLI was built ${String(days)} days ago, outside the ${String(CLI_CLIENT_WINDOW_DAYS)}-day window the API keeps working for: update it (git pull) — commands may fail`;
  }
  if (days > CLI_CLIENT_WARN_DAYS) return `this acp CLI was built ${String(days)} days ago: update it soon (git pull; the API keeps working for ${String(CLI_CLIENT_WINDOW_DAYS)} days)`;
  return null;
}
